/**
 * A corded scanner in keyboard mode.
 *
 * The gun is a keyboard as far as the browser is concerned: it types the code
 * and presses Enter. What separates it from a person is speed — a scanner
 * delivers a whole code at a few milliseconds per character, and nobody types
 * "L-7F3K2Q" at forty. That gap is the entire detector; there is no other signal
 * available, because the device does not identify itself.
 *
 * Two deliberate limits:
 *
 *   - Nothing is intercepted while a text field has the focus. There the
 *     scanner is doing exactly what it looks like it is doing, and the field's
 *     own handling of Enter is what should run. Grabbing those keystrokes would
 *     mean a search box that cannot be typed into quickly.
 *   - A burst without a terminating Enter is discarded. Every scanner can be
 *     configured to send one, ours do, and guessing on a timer instead would
 *     turn a fast typist into a phantom scan.
 */

/** Characters further apart than this are not the same burst. */
const MAX_GAP_MS = 35;

/** Shorter than this is not one of our codes, nor a barcode worth acting on. */
const MIN_LENGTH = 4;

export interface WedgeOptions {
  maxGapMs?: number;
  minLength?: number;
}

function typingIntoAField(target: EventTarget | null): boolean {
  const node = target as HTMLElement | null;
  if (!node) return false;
  if (node.isContentEditable) return true;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Watch for scans until the returned function is called.
 */
export function listenForWedge(
  onReading: (value: string) => void,
  options: WedgeOptions = {},
): () => void {
  const maxGap = options.maxGapMs ?? MAX_GAP_MS;
  const minLength = options.minLength ?? MIN_LENGTH;

  // Everything still in the buffer arrived at machine speed: a gap wider than
  // `maxGap` throws away what came before it, so no further check is needed at
  // the Enter — a person's typing never survives long enough to reach it.
  let buffer = '';
  let last = 0;

  const onKeyDown = (event: KeyboardEvent) => {
    if (typingIntoAField(event.target)) return;
    // A shortcut is not a scan. Shift is exempt: our codes are upper case, and
    // that is how the scanner sends them.
    if (event.ctrlKey || event.altKey || event.metaKey) return;

    const now = event.timeStamp || performance.now();
    const gap = now - last;
    last = now;

    if (event.key === 'Enter' || event.key === 'Tab') {
      const scanned = buffer;
      buffer = '';
      if (scanned.length >= minLength && gap <= maxGap) {
        // Enter from a scanner would otherwise submit whatever form the focus
        // happens to be sitting in.
        event.preventDefault();
        onReading(scanned);
      }
      return;
    }

    if (event.key.length !== 1) return;

    if (gap > maxGap) buffer = '';
    buffer += event.key;
  };

  window.addEventListener('keydown', onKeyDown, true);
  return () => window.removeEventListener('keydown', onKeyDown, true);
}
